import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { ROUTES } from 'config/navConfig';
import { useRole } from 'context/RoleContext';
import { ROLES, getRoleProfile } from 'config/roleConfig';
import {
  MARKETPLACE_STATES,
  MARKETPLACE_STATE_CONFIG,
  getMarketplaceStateConfig,
} from 'config/workflowStates';
import { MOCK_DEAL_ROOM_DEALS, MOCK_DEAL_ROOM_DOCUMENTS } from 'data/mockData';

// External Deal Room — scoped access for EXTERNAL_INVESTOR, DEAL_ORIGINATOR, CO_INVESTOR.
// This is NOT the internal Data Room. It shows only deal-room access appropriate to the
// external investor's current access stage.

function AccessBadge({ state }) {
  const cfg = getMarketplaceStateConfig(state);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold font-caption"
      style={{ color: cfg.color, background: cfg.bgColor }}
    >
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

function DocumentRow({ doc, accessState, isExternalInvestor }) {
  const hasAccess =
    accessState === MARKETPLACE_STATES.DD_ACCESS ||
    accessState === MARKETPLACE_STATES.DD_ACCESS_TIER_2 ||
    accessState === MARKETPLACE_STATES.SOFT_CIRCLED ||
    accessState === MARKETPLACE_STATES.COMMITTED ||
    accessState === MARKETPLACE_STATES.CAP_TABLE_ENTRY ||
    accessState === MARKETPLACE_STATES.CLOSING ||
    accessState === MARKETPLACE_STATES.CLOSED;

  const isLocked = !hasAccess && doc.tier === 'dd';

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors"
      style={{
        background: isLocked ? 'rgba(148,163,184,0.05)' : 'var(--color-muted)',
        opacity: isLocked ? 0.6 : 1,
      }}
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: isLocked ? 'rgba(148,163,184,0.15)' : 'rgba(59,76,76,0.08)' }}
      >
        <Icon
          name={isLocked ? 'Lock' : doc.ext === 'pdf' ? 'FileText' : doc.ext === 'xlsx' ? 'Table' : 'File'}
          size={15}
          color={isLocked ? '#94a3b8' : 'var(--color-primary)'}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-caption text-sm font-medium text-foreground truncate">{doc.name}</p>
        <p className="font-caption text-xs text-muted-foreground">{doc.category} · {doc.size}</p>
      </div>
      {isLocked ? (
        <span className="text-xs font-caption text-muted-foreground">
          {isExternalInvestor ? 'NDA required' : 'DD Access required'}
        </span>
      ) : (
        <button className="text-xs font-caption font-semibold" style={{ color: 'var(--color-primary)' }}>
          View
        </button>
      )}
    </div>
  );
}

export default function DealRoomPage() {
  const navigate = useNavigate();
  const { role, activeContext } = useRole();
  const profile = getRoleProfile(role);

  // Filter deal room entries by context — each deal's `contexts` array lists which demo
  // context IDs can see it. Platform admin sees everything (fallback).
  const visibleDeals = role === ROLES.PLATFORM_ADMIN
    ? MOCK_DEAL_ROOM_DEALS
    : MOCK_DEAL_ROOM_DEALS.filter((d) => !d.contexts || d.contexts.includes(activeContext?.id));

  const [selectedDeal, setSelectedDeal] = useState(visibleDeals[0]);
  const [dealStateOverrides, setDealStateOverrides] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const handleSignNDA = (dealId) => {
    setDealStateOverrides((prev) => ({ ...prev, [dealId]: MARKETPLACE_STATES.NDA_SIGNED }));
    showToast('NDA signed — due diligence documents are now accessible.');
  };

  // Compute effective access state, taking overrides into account
  const effectiveAccessState = (deal) =>
    deal ? (dealStateOverrides[deal.id] || deal.accessState) : null;

  // If somehow an internal role lands here, show a redirect notice
  if (role !== ROLES.EXTERNAL_INVESTOR && role !== ROLES.DEAL_ORIGINATOR && role !== ROLES.CO_INVESTOR) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-sm">
          <Icon name="FolderLock" size={40} color="var(--color-primary)" />
          <h2 className="font-heading font-semibold text-foreground text-xl mt-4">Internal Data Room</h2>
          <p className="font-caption text-muted-foreground text-sm mt-2">
            You are viewing as an internal role. The internal Data Room is accessible from Operations.
          </p>
          <Button
            variant="default"
            className="mt-5"
            onClick={() => navigate(ROUTES.dataRoom)}
          >
            Go to Internal Data Room
          </Button>
        </div>
      </div>
    );
  }

  const activeState = effectiveAccessState(selectedDeal);
  const accessCfg = activeState ? getMarketplaceStateConfig(activeState) : null;

  const progressStages = [
    MARKETPLACE_STATES.LISTED,
    MARKETPLACE_STATES.NDA_SIGNED,
    MARKETPLACE_STATES.DD_ACCESS,
    MARKETPLACE_STATES.COMMITTED,
    MARKETPLACE_STATES.CLOSED,
  ];

  const currentProgressStep = activeState
    ? progressStages.findIndex(
        (s) =>
          MARKETPLACE_STATE_CONFIG[activeState]?.phase ===
          MARKETPLACE_STATE_CONFIG[s]?.phase
      )
    : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* External Deal Room Header */}
      <header className="sticky top-0 z-50 bg-background border-b border-border px-6 md:px-10 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.1)' }}>
            <Icon name="FolderOpen" size={17} color="#7c3aed" />
          </div>
          <div>
            <span className="font-heading font-semibold text-foreground text-base">
              {role === ROLES.EXTERNAL_INVESTOR ? 'Co-Investment Data Room' : 'Deal Room'}
            </span>
            <span className="hidden sm:inline font-caption text-muted-foreground text-xs ml-2">
              {role === ROLES.EXTERNAL_INVESTOR ? 'GP due diligence access' : 'External investor access'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Role badge */}
          <div
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-caption font-semibold"
            style={{ background: `${profile.color}15`, color: profile.color }}
          >
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ background: profile.color, fontSize: '0.6rem' }}
            >
              {profile.initials}
            </span>
            {profile.label}
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="TrendingUp"
            iconPosition="left"
            iconSize={14}
            onClick={() => navigate(ROUTES.opportunities)}
          >
            Opportunities
          </Button>
        </div>
      </header>

      <div className="px-6 md:px-10 lg:px-12 py-6 md:py-8">

        {/* Access Stage Progress */}
        {selectedDeal && (
          <div className="card px-6 py-5 mb-5">
            <div className="flex items-center justify-between mb-4">
              <p className="font-caption text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Your Access Stage — {selectedDeal.name}
              </p>
              <AccessBadge state={activeState} />
            </div>
            {/* Progress bar */}
            <div className="flex items-center gap-0">
              {progressStages.map((stage, idx) => {
                const cfg    = MARKETPLACE_STATE_CONFIG[stage];
                const isDone = currentProgressStep > idx;
                const isCurr = currentProgressStep === idx;
                const isLast = idx === progressStages.length - 1;
                return (
                  <React.Fragment key={stage}>
                    <div className="flex flex-col items-center">
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{
                          background: isDone || isCurr ? cfg.color : 'var(--color-muted)',
                          color: isDone || isCurr ? 'white' : 'var(--color-muted-foreground)',
                        }}
                      >
                        {isDone ? <Icon name="Check" size={12} /> : idx + 1}
                      </div>
                      <span className="text-center font-caption mt-1" style={{ fontSize: '0.65rem', color: isCurr ? cfg.color : 'var(--color-muted-foreground)', fontWeight: isCurr ? 600 : 400, whiteSpace: 'nowrap' }}>
                        {cfg.label}
                      </span>
                    </div>
                    {!isLast && (
                      <div
                        className="h-0.5 flex-1 mx-1 mb-4"
                        style={{ background: isDone ? cfg.color : 'var(--color-border)' }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Deal List */}
          <div className="md:col-span-1 card overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="font-caption text-xs font-semibold uppercase tracking-wider text-muted-foreground">Your Deals</p>
            </div>
            <div className="py-2">
              {visibleDeals.map((deal) => (
                <div
                  key={deal.id}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors rounded-lg mx-2"
                  style={{
                    background: selectedDeal?.id === deal.id ? 'rgba(59,76,76,0.06)' : 'transparent',
                    borderLeft: selectedDeal?.id === deal.id ? '2px solid var(--color-primary)' : '2px solid transparent',
                  }}
                  onClick={() => setSelectedDeal(deal)}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold text-white" style={{ background: 'var(--color-primary)' }}>
                    {deal.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-caption text-sm font-medium text-foreground truncate">{deal.name}</p>
                    <p className="font-caption text-xs text-muted-foreground">{deal.sector}</p>
                  </div>
                  <AccessBadge state={dealStateOverrides[deal.id] || deal.accessState} />
                </div>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div className="md:col-span-2 card overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <p className="font-caption text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {selectedDeal ? `${selectedDeal.name} — Documents` : 'Documents'}
              </p>
              {selectedDeal && accessCfg?.ctaAction === 'sign_nda' && (
                <Button
                  variant="default"
                  size="sm"
                  iconName="FileCheck"
                  iconPosition="left"
                  iconSize={13}
                  onClick={() => handleSignNDA(selectedDeal.id)}
                >
                  Sign NDA to Unlock
                </Button>
              )}
            </div>
            {selectedDeal && (
              <div className="p-4 space-y-2">
                {MOCK_DEAL_ROOM_DOCUMENTS
                  .filter((d) => d.dealId === selectedDeal.id)
                  .map((doc) => (
                    <DocumentRow key={doc.id} doc={doc} accessState={activeState} isExternalInvestor={role === ROLES.EXTERNAL_INVESTOR} />
                  ))}
              </div>
            )}
            {/* Access required notice — shown only for LISTED/ACCESS_REQUESTED/NDA_PENDING */}
            {selectedDeal &&
              (activeState === MARKETPLACE_STATES.LISTED ||
                activeState === MARKETPLACE_STATES.ACCESS_REQUESTED ||
                activeState === MARKETPLACE_STATES.NDA_PENDING) && (
              <div
                className="mx-4 mb-4 p-4 rounded-xl text-center"
                style={{ background: 'rgba(217,119,6,0.07)', border: '1px dashed rgba(217,119,6,0.3)' }}
              >
                <Icon name="Lock" size={20} color="#d97706" />
                <p className="font-caption text-sm font-semibold text-foreground mt-2">
                  NDA Required for Document Access
                </p>
                <p className="font-caption text-xs text-muted-foreground mt-1">
                  Complete the NDA process to access deal room documents.
                </p>
                <Button
                  variant="default"
                  size="sm"
                  className="mt-3"
                  iconName="FileCheck"
                  iconPosition="left"
                  iconSize={13}
                  onClick={() => handleSignNDA(selectedDeal.id)}
                >
                  Sign NDA
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Co-Investment Terms card — co-investor only, DD_ACCESS or higher */}
        {role === ROLES.CO_INVESTOR && selectedDeal?.coInvestmentTerms && (
          activeState === MARKETPLACE_STATES.DD_ACCESS ||
          activeState === MARKETPLACE_STATES.DD_ACCESS_TIER_2 ||
          activeState === MARKETPLACE_STATES.SOFT_CIRCLED ||
          activeState === MARKETPLACE_STATES.COMMITTED ||
          activeState === MARKETPLACE_STATES.CAP_TABLE_ENTRY ||
          activeState === MARKETPLACE_STATES.CLOSING ||
          activeState === MARKETPLACE_STATES.CLOSED
        ) && (
          <div className="card px-6 py-5 mt-5">
            <h3 className="font-caption text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
              <Icon name="FileSignature" size={14} color="var(--color-primary)" />
              Co-Investment Terms — {selectedDeal.name}
            </h3>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4">
              {Object.entries(selectedDeal.coInvestmentTerms).map(([k, v]) => (
                <div key={k}>
                  <dt className="font-caption text-xs text-muted-foreground capitalize mb-0.5">
                    {k.replace(/([A-Z])/g, ' $1').trim()}
                  </dt>
                  <dd className="font-caption text-sm font-semibold text-foreground">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex items-center justify-between">
          <p className="font-caption text-xs text-muted-foreground">
            <Icon name="Lock" size={11} className="inline mr-1" />
            Secure deal room · Access scoped to your investor profile · GDPR compliant
          </p>
          <Button
            variant="ghost"
            size="sm"
            iconName="HelpCircle"
            iconPosition="left"
            iconSize={13}
          >
            Contact Deal Team
          </Button>
        </div>
      </div>

      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-white text-sm font-caption font-medium"
          style={{ background: 'var(--color-primary)', minWidth: '280px' }}
        >
          <Icon name="CheckCircle" size={17} color="rgba(148,197,255,0.9)" />
          {toast}
        </div>
      )}
    </div>
  );
}
